<script lang="ts">
	import { Button, Text, Toggle } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig, VentoraHumConfig } from 'src/types/ventora_fan';
	import VentoraFanNumericInput from './VentoraFanNumericInput.svelte';
	import {
		VENTORA_HUM_OFF_DELAY_HINT,
		VENTORA_HUM_SPEED_HINT,
		VENTORA_HUM_THRESHOLD_HINT,
		VENTORA_HUM_TRIGGER_PRIORITY_HINT,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraClamp,
		ventoraFiniteNumberFromInput,
		ventoraHasValidationErrors,
		ventoraHumLimits,
		ventoraNumericValidation,
		ventoraToNumber,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	type HumNumericField = keyof Pick<VentoraHumConfig, 'speed' | 'thr' | 'off_delay'>;

	let loading = false;
	let dirty = false;
	let draft: VentoraHumConfig = { ...defaultVentoraFanConfig.hum };

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draft = { ...config.hum };
	}

	$: speedValidation = ventoraNumericValidation(draft.speed, ...ventoraHumLimits.speed);
	$: thresholdValidation = ventoraNumericValidation(draft.thr, ...ventoraHumLimits.thr);
	$: offDelayValidation = ventoraNumericValidation(draft.off_delay, ...ventoraHumLimits.off_delay);
	$: hasValidationErrors = ventoraHasValidationErrors([
		speedValidation,
		thresholdValidation,
		offDelayValidation,
	]);

	const inputField = (field: HumNumericField, e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draft = { ...draft, [field]: n };
	};

	const blurField = (field: HumNumericField, e: Event) => {
		const [min, max] = ventoraHumLimits[field];
		const raw = (e.currentTarget as HTMLInputElement).value;
		draft = {
			...draft,
			[field]: ventoraClamp(ventoraToNumber(raw, draft[field]), min, max),
		};
	};

	const commitHumDraft = () => {
		draft = {
			...draft,
			speed: ventoraClamp(draft.speed, ...ventoraHumLimits.speed),
			thr: ventoraClamp(draft.thr, ...ventoraHumLimits.thr),
			off_delay: ventoraClamp(draft.off_delay, ...ventoraHumLimits.off_delay),
		};
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		commitHumDraft();
		loading = true;
		const ok = await patchVentoraFanConfig(device, { hum: { ...draft } });
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format(VENTORA_HUM_TRIGGER_PRIORITY_HINT.key, {
		default: VENTORA_HUM_TRIGGER_PRIORITY_HINT.default,
	})}
</Text>

<Toggle
	size="m"
	class="mb-m"
	checked={draft.enable}
	on:click={() => {
		draft = { ...draft, enable: !draft.enable };
		dirty = true;
	}}
	id="toggle-ventora-hum-trigger"
>
	{$format('ventora_hum_enable', { default: 'Enable humidity trigger' })}
</Toggle>

<VentoraFanNumericInput
	_id="input-ventora-hum-speed"
	labelKey="ventora_hum_speed"
	labelDefault="Speed"
	hintKey={VENTORA_HUM_SPEED_HINT.key}
	hintDefault={VENTORA_HUM_SPEED_HINT.default}
	value={draft.speed}
	min={ventoraHumLimits.speed[0]}
	max={ventoraHumLimits.speed[1]}
	icon="fas fa-fan"
	validation={speedValidation}
	onInput={(e) => inputField('speed', e)}
	onBlur={(e) => blurField('speed', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-hum-threshold"
	labelKey="ventora_hum_threshold"
	labelDefault="Threshold (%RH)"
	hintKey={VENTORA_HUM_THRESHOLD_HINT.key}
	hintDefault={VENTORA_HUM_THRESHOLD_HINT.default}
	value={draft.thr}
	min={ventoraHumLimits.thr[0]}
	max={ventoraHumLimits.thr[1]}
	icon="fas fa-droplet-percent"
	validation={thresholdValidation}
	onInput={(e) => inputField('thr', e)}
	onBlur={(e) => blurField('thr', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-hum-off-delay"
	labelKey="ventora_hum_off_delay"
	labelDefault="Off delay (seconds)"
	hintKey={VENTORA_HUM_OFF_DELAY_HINT.key}
	hintDefault={VENTORA_HUM_OFF_DELAY_HINT.default}
	value={draft.off_delay}
	min={ventoraHumLimits.off_delay[0]}
	max={ventoraHumLimits.off_delay[1]}
	icon="fas fa-hourglass-end"
	validation={offDelayValidation}
	onInput={(e) => inputField('off_delay', e)}
	onBlur={(e) => blurField('off_delay', e)}
/>

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-hum-trigger-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
